import { lerp } from './utils/math-helpers.js';

export function createTerrainOps({
  WORLD_MIN_Y,
  WATER_LEVEL,
  clamp,
  smooth,
  fbm2,
  valueNoise2,
  valueNoise3,
  toLocalX,
  ecosystemScale = 1,
  terrainVerticalScale = 1,
  undergroundDepthScale = 1
}) {
  const ecoScale = Math.max(0.75, Number(ecosystemScale) || 1);
  const ecoMediumScale = Math.max(1, Math.sqrt(ecoScale));
  const reliefScale = Math.max(0.8, Number(terrainVerticalScale) || 1);
  const depthScale = Math.max(0.8, Number(undergroundDepthScale) || 1);
  const DEEP_LAVA_MAX_Y = Math.floor(12 + Math.max(0, depthScale - 1) * 6);
  const DEEP_LAVA_THRESHOLD = 0.72;
  const IRON_ORE_MAX_Y = Math.floor(60 + Math.max(0, depthScale - 1) * 16);
  const IRON_ORE_THRESHOLD = 0.25;
  const COAL_ORE_MAX_Y = Math.floor(78 + Math.max(0, depthScale - 1) * 12);
  const COAL_ORE_THRESHOLD = 0.4;

  function scaledFreq(baseFreq, band = 'macro') {
    if (band === 'macro') return baseFreq / ecoScale;
    if (band === 'medium') return baseFreq / ecoMediumScale;
    return baseFreq;
  }

  function columnNoise(x, z) {
    const macro = fbm2(x, z, { freq: scaledFreq(0.0028, 'macro'), octaves: 4, gain: 0.52, lacunarity: 2.05, seed: 13 });
    const hills = fbm2(x, z, { freq: scaledFreq(0.011, 'medium'), octaves: 4, gain: 0.52, lacunarity: 2.0, seed: 47 });
    const ridgeSrc = fbm2(x, z, { freq: scaledFreq(0.0056, 'medium'), octaves: 3, gain: 0.55, lacunarity: 2.1, seed: 91 });
    const ridge = 1 - Math.abs(ridgeSrc);
    const mountainField = (fbm2(x, z, { freq: scaledFreq(0.0038, 'macro'), octaves: 3, gain: 0.5, lacunarity: 2.0, seed: 109 }) + 1) * 0.5;
    const valleyField = (fbm2(x, z, { freq: scaledFreq(0.009, 'medium'), octaves: 3, gain: 0.5, lacunarity: 2.0, seed: 211 }) + 1) * 0.5;
    const plateauField = (fbm2(x + 37, z - 19, { freq: scaledFreq(0.0046, 'medium'), octaves: 3, gain: 0.54, lacunarity: 2.0, seed: 223 }) + 1) * 0.5;
    const canyonRidge = 1 - Math.abs(fbm2(x + 101, z - 89, { freq: scaledFreq(0.0064, 'medium'), octaves: 3, gain: 0.56, lacunarity: 2.1, seed: 227 }));
    const ravineRidge = 1 - Math.abs(fbm2(x - 73, z + 61, { freq: scaledFreq(0.0125, 'medium'), octaves: 3, gain: 0.55, lacunarity: 2.05, seed: 233 }));
    const volcanoField = (fbm2(x - 53, z + 71, { freq: scaledFreq(0.0034, 'macro'), octaves: 3, gain: 0.54, lacunarity: 2.02, seed: 257 }) + 1) * 0.5;
    const volcanoCone = 1 - Math.abs(fbm2(x + 131, z - 43, { freq: scaledFreq(0.0074, 'medium'), octaves: 4, gain: 0.55, lacunarity: 2.07, seed: 263 }));
    const hillField = (fbm2(x + 13, z + 17, { freq: scaledFreq(0.0135, 'medium'), octaves: 3, gain: 0.52, lacunarity: 2.0, seed: 271 }) + 1) * 0.5;
    const lakeField = (fbm2(x, z, { freq: scaledFreq(0.02, 'medium'), octaves: 3, gain: 0.55, lacunarity: 2.0, seed: 307 }) + 1) * 0.5;
    const humidity = (fbm2(x, z, { freq: scaledFreq(0.008, 'macro'), octaves: 4, gain: 0.56, lacunarity: 2.0, seed: 411 }) + 1) * 0.5;
    const temperature = (fbm2(x, z, { freq: scaledFreq(0.007, 'macro'), octaves: 3, gain: 0.5, lacunarity: 2.0, seed: 503 }) + 1) * 1.2;

    const ridgeRelief = 1 + ((reliefScale - 1) * 0.86);
    const valleyRelief = 1 + ((reliefScale - 1) * 0.55);
    const canyonRelief = 1 + ((depthScale - 1) * 0.9);
    let h = 7 + macro * (8.8 * ridgeRelief) + hills * (3.8 * (0.95 + (reliefScale - 1) * 0.4));
    h += ridge * (19 * reliefScale) * smooth(clamp((mountainField - 0.47) / 0.45, 0, 1));
    h -= smooth(clamp((valleyField - 0.44) / 0.45, 0, 1)) * (7.5 * valleyRelief);

    const plateauMask = smooth(clamp((plateauField - 0.6) / 0.35, 0, 1))
      * smooth(clamp((mountainField - 0.28) / 0.58, 0, 1));
    if (plateauMask > 0.08) {
      const plateauHeight = (18 * reliefScale) + macro * (8.4 * ridgeRelief) + hills * (1.4 * valleyRelief) + plateauMask * (9.2 * reliefScale);
      const blend = 0.34 + plateauMask * 0.42;
      h = lerp(h, plateauHeight, blend);
      h = Math.round(h * 0.5) * 2;
    }

    const canyonCut = smooth(clamp((canyonRidge - 0.74) / 0.23, 0, 1));
    const ravineCut = smooth(clamp((ravineRidge - 0.77) / 0.2, 0, 1));
    h -= canyonCut * (9 + mountainField * 12) * canyonRelief;
    h -= ravineCut * (7 + ((hills + 1) * 0.5) * 9) * canyonRelief;

    const lakeCut = smooth(clamp((lakeField - 0.59) / 0.31, 0, 1));
    if (lakeCut > 0.15 && mountainField < 0.62) {
      h -= lakeCut * (9.5 * Math.min(1.55, canyonRelief));
    }

    const heatMask = smooth(clamp((temperature - 0.54) / 0.44, 0, 1));
    const dryMask = smooth(clamp((0.64 - humidity) / 0.64, 0, 1));
    const volcanoMask = smooth(clamp((volcanoField - 0.69) / 0.28, 0, 1)) * heatMask * dryMask;
    if (volcanoMask > 0.12) {
      const coneBoost = volcanoMask * ((18 * reliefScale) + (volcanoCone * 24 * reliefScale) + (macro * 6.2 * reliefScale));
      h += coneBoost;
      const craterMask = volcanoMask * smooth(clamp((volcanoCone - 0.82) / 0.18, 0, 1));
      if (craterMask > 0.05) {
        h -= craterMask * (12 * depthScale + volcanoMask * 7.5);
      }
    }

    const hillMask = smooth(clamp((hillField - 0.56) / 0.32, 0, 1)) * smooth(clamp(1 - volcanoMask * 1.7, 0, 1));
    if (hillMask > 0.1 && lakeCut < 0.65 && canyonCut < 0.45 && ravineCut < 0.45) {
      h += hillMask * (5.2 * reliefScale);
    }

    const maxSurfaceY = Math.round(46 + Math.max(0, reliefScale - 1) * 34);
    h = Math.floor(clamp(h, WORLD_MIN_Y + 3, maxSurfaceY));
    const highland = h > (24 + Math.max(0, reliefScale - 1) * 9) || mountainField > 0.63 || plateauMask > 0.5;
    let biome = 'lowland';
    if (h <= WATER_LEVEL - 4) biome = 'ocean';
    else if (h <= WATER_LEVEL + 1 || lakeCut > 0.62) biome = 'wetland';
    else if (highland) biome = temperature < 0.35 ? 'snow' : 'mountain';
    else if (temperature > 0.65 && humidity < 0.45) biome = 'desert';
    else if (temperature < 0.3) biome = 'snow';
    else if (humidity > 0.48) biome = 'forest';

    let terrain = highland ? 'mountain' : 'plains';
    if (plateauMask > 0.55 && h > 17 && canyonCut < 0.42) terrain = 'plateau';
    if (volcanoMask > 0.33 && h > WATER_LEVEL + 8 && canyonCut < 0.55 && ravineCut < 0.55) {
      terrain = 'volcano';
      biome = 'mountain';
    } else if (hillMask > 0.48 && !highland && h > WATER_LEVEL + 3 && canyonCut < 0.5 && ravineCut < 0.5) {
      terrain = 'hill';
    }
    if (canyonCut > 0.52) terrain = 'canyon';
    else if (ravineCut > 0.5) terrain = 'ravine';

    return {
      height: h,
      biome,
      humidity,
      terrain,
      canyon: canyonCut,
      ravine: ravineCut,
      plateau: plateauMask,
      volcano: volcanoMask,
      hill: hillMask
    };
  }

  function netherColumnNoise(worldX, worldZ) {
    const x = toLocalX(worldX, 'nether');
    const z = worldZ;
    const macro = fbm2(x, z, { freq: scaledFreq(0.0052, 'medium'), octaves: 4, gain: 0.54, lacunarity: 2.05, seed: 3001 });
    const ridges = 1 - Math.abs(fbm2(x, z, { freq: scaledFreq(0.0105, 'medium'), octaves: 3, gain: 0.56, lacunarity: 2.1, seed: 3017 }));
    const valley = (fbm2(x, z, { freq: scaledFreq(0.016, 'medium'), octaves: 3, gain: 0.5, lacunarity: 2.0, seed: 3031 }) + 1) * 0.5;
    const soul = (fbm2(x, z, { freq: scaledFreq(0.02, 'medium'), octaves: 3, gain: 0.52, lacunarity: 2.0, seed: 3049 }) + 1) * 0.5;

    let h = 14 + macro * (9 * reliefScale) + ridges * (4.5 * reliefScale);
    h -= smooth(clamp((valley - 0.56) / 0.42, 0, 1)) * (5.2 * (1 + (depthScale - 1) * 0.4));
    h = Math.floor(clamp(h, WORLD_MIN_Y + 5, Math.round(36 + Math.max(0, reliefScale - 1) * 11)));

    return {
      height: h,
      biome: soul > 0.62 ? 'soul_valley' : 'nether_wastes',
      soul
    };
  }

  function endColumnNoise(worldX, worldZ) {
    const x = toLocalX(worldX, 'end');
    const z = worldZ;
    const dist = Math.hypot(x, z);
    const base = (fbm2(x, z, { freq: scaledFreq(0.009, 'medium'), octaves: 4, gain: 0.53, lacunarity: 2.05, seed: 4091 }) + 1) * 0.5;
    const ridge = 1 - Math.abs(fbm2(x + 71, z - 53, { freq: scaledFreq(0.015, 'medium'), octaves: 3, gain: 0.56, lacunarity: 2.1, seed: 4111 }));
    const mainIsland = smooth(clamp((145 - dist) / 145, 0, 1));
    const outerBand = smooth(clamp((dist - 120) / 220, 0, 1));
    const outerIslands = outerBand * smooth(clamp((base - 0.57) / 0.33, 0, 1));

    const land = Math.max(mainIsland, outerIslands * 0.95);
    if (land <= 0.02) {
      return { hasLand: false, height: WORLD_MIN_Y + 1, crown: 0 };
    }

    const crown = smooth(clamp((ridge - 0.47) / 0.53, 0, 1));
    const h = Math.floor(clamp(-2 + land * (23 * reliefScale) + crown * (5 * reliefScale), WORLD_MIN_Y + 3, Math.round(42 + Math.max(0, reliefScale - 1) * 12)));
    return {
      hasLand: true,
      height: h,
      crown
    };
  }

  function chooseStoneType(x, y, z) {
    if (y <= 2) {
      const obsidian = valueNoise3(x, y, z, 0.15, 1093);
      if (obsidian > 0.57) return 31;
    }
    if (y <= 6) {
      const diamond = valueNoise3(x, y, z, 0.14, 1117);
      if (diamond > 0.52) return 10;
    }
    if (y <= Math.floor(24 + Math.max(0, depthScale - 1) * 14)) {
      const gold = valueNoise3(x, y, z, 0.128, 743);
      if (gold > 0.48) return 47;
    }
    if (y <= IRON_ORE_MAX_Y) {
      const iron = valueNoise3(x, y, z, 0.12, 701);
      if (iron > IRON_ORE_THRESHOLD) return 8;
    }
    if (y <= COAL_ORE_MAX_Y) {
      const coal = valueNoise3(x, y, z, 0.135, 907);
      if (coal > COAL_ORE_THRESHOLD) return 9;
    }
    if (y <= DEEP_LAVA_MAX_Y) {
      const lava = valueNoise3(x - 17, y, z + 23, 0.118, 1601);
      if (lava > DEEP_LAVA_THRESHOLD) return 69;
    }
    return 5;
  }

  function shouldCarveCave(x, y, z, surfaceY, biome) {
    if (y >= surfaceY - 3) return false;
    if (y <= WORLD_MIN_Y + 1) return false;
    if (biome === 'wetland' && y > WATER_LEVEL - 2) return false;

    const chamber = (valueNoise3(x, y, z, scaledFreq(0.056, 'medium'), 1911) + 1) * 0.5;
    const tunnels = (valueNoise3(x + 19, y * 1.1, z - 13, scaledFreq(0.104, 'medium'), 1931) + 1) * 0.5;
    const veins = (valueNoise3(x - 37, y * 0.9, z + 23, scaledFreq(0.028, 'medium'), 1973) + 1) * 0.5;
    const caveField = (chamber * 0.6) + (tunnels * 0.28) + (veins * 0.12);

    const depth = clamp((surfaceY - y) / (20 * depthScale), 0, 1);
    const threshold = 0.79 - (depth * 0.11);
    return caveField > threshold;
  }

  function shouldCarveSurfaceRavine(x, y, z, surfaceY, info) {
    const terrain = info?.terrain;
    if (terrain !== 'canyon' && terrain !== 'ravine') return false;
    if (y > surfaceY - 1) return false;
    if (y <= WORLD_MIN_Y + 1) return false;

    const cutStrength = Math.max(Number(info?.canyon || 0), Number(info?.ravine || 0));
    if (cutStrength <= 0.34) return false;

    const axisNoiseFreq = scaledFreq(terrain === 'canyon' ? 0.05 : 0.074, 'medium');
    const axisNoise = Math.abs(valueNoise2(x + 47, z - 31, axisNoiseFreq, terrain === 'canyon' ? 7703 : 7709));
    const width = terrain === 'canyon'
      ? (0.16 + (1 - cutStrength) * 0.07)
      : (0.1 + (1 - cutStrength) * 0.05);
    if (axisNoise > width) return false;

    const baseDepth = (terrain === 'canyon' ? 8 : 6) * depthScale;
    const extraDepth = (terrain === 'canyon' ? 15 : 10) * depthScale;
    const depth = Math.floor(baseDepth + cutStrength * extraDepth);
    const floorY = surfaceY - depth;
    if (y < floorY) return false;

    const wallFalloff = clamp(1 - (axisNoise / Math.max(0.0001, width)), 0, 1);
    const roofTrim = surfaceY - Math.floor(1 + wallFalloff * (terrain === 'canyon' ? 3 : 2));
    return y <= roofTrim;
  }

  function shouldCarveNetherCave(worldX, y, worldZ, surfaceY) {
    if (y >= surfaceY - 1) return false;
    if (y <= WORLD_MIN_Y + 2) return false;

    const x = toLocalX(worldX, 'nether');
    const z = worldZ;
    const chamber = (valueNoise3(x, y, z, scaledFreq(0.06, 'medium'), 3067) + 1) * 0.5;
    const tunnels = (valueNoise3(x + 23, y * 1.05, z - 17, scaledFreq(0.11, 'medium'), 3089) + 1) * 0.5;
    const caveField = chamber * 0.64 + tunnels * 0.36;
    const depth = clamp((surfaceY - y) / (18 * depthScale), 0, 1);
    return caveField > (0.8 - depth * 0.1);
  }

  function shouldCarveEndVoid(worldX, y, worldZ, surfaceY) {
    if (y >= surfaceY - 2) return false;
    if (y <= WORLD_MIN_Y + 2) return false;

    const x = toLocalX(worldX, 'end');
    const z = worldZ;
    const field = (valueNoise3(x, y, z, scaledFreq(0.07, 'medium'), 4153) + 1) * 0.5;
    const depth = clamp((surfaceY - y) / (16 * depthScale), 0, 1);
    return field > (0.82 - depth * 0.12);
  }

  return {
    columnNoise,
    netherColumnNoise,
    endColumnNoise,
    chooseStoneType,
    shouldCarveCave,
    shouldCarveSurfaceRavine,
    shouldCarveNetherCave,
    shouldCarveEndVoid
  };
}
